import { useEffect, useState, useCallback } from "react";
import api, { extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import {
  Check, X, ChevronDown, RotateCcw, PenLine, Info,
  GraduationCap, ExternalLink, ArrowRight,
} from "lucide-react";
import { formatQidRefs } from "@/lib/qidFormat";

/**
 * SkillGapPanel — reusable panel that renders one direction's skill_gap
 * data from Stage 1's endpoints. Shows five sections + priority ordering,
 * with correct/reject controls wired to POST /api/skill-gaps/action.
 *
 * Props:
 *   directionId     : string (required)
 *   answersById?    : { qid -> {answer, ...} }  (optional; expands evidence)
 *   questionMap?    : { qid -> question def }   (optional; expands evidence)
 */
export function SkillGapPanel({ directionId, answersById, questionMap }) {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/skill-gaps/for-direction/${directionId}`);
      setRow(data?.skill_gap_row || null);
    } catch (err) {
      setError(extractApiError(err, "Couldn't load the skill-gap panel."));
    } finally {
      setLoading(false);
    }
  }, [directionId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div data-testid={`sg-panel-loading-${directionId}`} className="rounded-lg border border-border-subtle bg-bg-surface p-4 text-xs text-fg-secondary">
        Loading skill-gap details…
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" data-testid={`sg-panel-error-${directionId}`}>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!row) {
    return (
      <div data-testid={`sg-panel-empty-${directionId}`} className="rounded-lg border border-border-subtle bg-bg-surface p-4 text-xs text-fg-secondary italic">
        No skill-gap analysis is stored for this direction yet. If this is an
        older direction set (before M-W7 Stage 1), regenerate to create one.
      </div>
    );
  }

  const sg = row.skill_gap || {};

  // Merge start_now / learn_next / external into an owner-specified
  // priority ordering.
  const priorityGroups = [
    { key: "start_now",             label: "Start now",             tone: "border-accent-warm/40 bg-accent-warm/10 text-accent-warm",     items: (sg.gaps || []).filter((i) => i.priority === "start_now") },
    { key: "learn_next",            label: "Learn next",            tone: "border-progress-teal/40 bg-progress-teal/10 text-progress-teal", items: (sg.gaps || []).filter((i) => i.priority === "learn_next" || !i.priority) },
    { key: "external_requirement",  label: "External requirement",  tone: "border-fg-secondary/40 bg-bg-surface-hover text-fg-secondary",  items: [...(sg.external_requirements || [])] },
  ];

  return (
    <div data-testid={`sg-panel-${directionId}`} className="space-y-6">
      {/* Header row */}
      <div className="rounded-md border border-border-subtle bg-bg-base p-3">
        <p className="text-[11px] uppercase tracking-wider text-fg-secondary">
          Skill-gap analysis
        </p>
        <p className="mt-1 text-xs text-fg-secondary">
          Every row is a draft. You can correct any label or reject anything that isn't right.
        </p>
      </div>

      {/* What you already bring */}
      <PanelSection
        testid={`sg-panel-${directionId}-have`}
        title="What you already bring"
        subtitle="Evidence-linked — each row cites the questions you answered."
      >
        <ItemList
          items={sg.already_have || []}
          directionId={directionId}
          onChanged={load}
          answersById={answersById}
          questionMap={questionMap}
          kind="already_have"
        />
      </PanelSection>

      {/* What this direction requires */}
      <PanelSection
        testid={`sg-panel-${directionId}-req`}
        title="What this direction requires"
      >
        <ItemList
          items={sg.direction_requires || []}
          directionId={directionId}
          onChanged={load}
          kind="direction_requires"
        />
      </PanelSection>

      {/* The gap — priority-grouped */}
      <PanelSection
        testid={`sg-panel-${directionId}-gap`}
        title="The gap"
        subtitle="Ordered by what fits your energy first."
      >
        <div className="space-y-3">
          {priorityGroups.map((g) => (
            g.items.length > 0 && (
              <div key={g.key} data-testid={`sg-panel-${directionId}-priority-${g.key}`}>
                <span className={"text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border inline-block mb-2 " + g.tone}>
                  {g.label}
                </span>
                <ItemList
                  items={g.items}
                  directionId={directionId}
                  onChanged={load}
                  kind={g.key === "external_requirement" ? "external_requirement" : "gap"}
                  externalLabel={g.key === "external_requirement"}
                />
              </div>
            )
          ))}
          {priorityGroups.every((g) => g.items.length === 0) && (
            <p data-testid={`sg-panel-${directionId}-gap-empty`} className="text-xs text-fg-muted italic">
              No gap items in the current draft.
            </p>
          )}
        </div>
      </PanelSection>

      {/* What CRA3D teaches */}
      <PanelSection
        testid={`sg-panel-${directionId}-teaches`}
        title="What CRA3D teaches"
        subtitle="Deterministic mapping — links only where CRA3D actually covers the skill."
      >
        <TeachesList gaps={sg.gaps || []} directionId={directionId} />
      </PanelSection>
    </div>
  );
}


function PanelSection({ title, subtitle, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-lg border border-border-subtle bg-bg-surface p-5">
      <p className="text-xs uppercase tracking-wider text-fg-secondary">{title}</p>
      {subtitle && <p className="mt-1 text-xs text-fg-muted">{subtitle}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}


function ItemList({ items, directionId, onChanged, answersById, questionMap, kind, externalLabel }) {
  if (!items || !items.length) {
    return <p className="text-xs text-fg-muted italic">Nothing recorded.</p>;
  }
  return (
    <ul className="space-y-3">
      {items.map((it) => (
        <SkillItem
          key={it.id || `${kind}-${it.label}`}
          item={it}
          directionId={directionId}
          onChanged={onChanged}
          answersById={answersById}
          questionMap={questionMap}
          externalLabel={externalLabel}
        />
      ))}
    </ul>
  );
}


function SkillItem({ item, directionId, onChanged, answersById, questionMap, externalLabel }) {
  const [editOpen, setEditOpen] = useState(false);
  const [newLabel, setNewLabel] = useState(item.corrected_label || item.label || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [openEvidence, setOpenEvidence] = useState(false);

  const displayLabel = item.corrected ? (item.corrected_label || item.label) : item.label;
  const isRejected = !!item.rejected;

  const send = async (action, payload = {}) => {
    setBusy(true);
    try {
      await api.post("/skill-gaps/action", {
        direction_id: directionId,
        item_id: item.id,
        action,
        ...payload,
      });
      const msg = {
        correct:   "Label updated.",
        uncorrect: "Correction cleared.",
        reject:    "Item marked as not right.",
        unreject:  "Item restored.",
      }[action];
      if (msg) toast.success(msg);
      setEditOpen(false);
      setReason("");
      onChanged?.();
    } catch (err) {
      toast.error(extractApiError(err, "Action failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li
      data-testid={`sg-item-${item.id}`}
      data-item-id={item.id}
      data-corrected={item.corrected || false}
      data-rejected={isRejected}
      className={
        "rounded-md border p-3 " +
        (isRejected
          ? "border-destructive/40 bg-bg-surface opacity-60"
          : "border-border-subtle bg-bg-base")
      }
    >
      <div className="flex flex-wrap items-start gap-2">
        <p data-testid={`sg-item-${item.id}-label`} className="flex-1 text-sm text-fg-primary">
          {formatQidRefs(displayLabel)}
          {item.corrected && (
            <span data-testid={`sg-item-${item.id}-corrected-badge`} className="ml-2 text-[10px] uppercase tracking-wider text-progress-teal">
              (corrected)
            </span>
          )}
          {externalLabel && (
            <span data-testid={`sg-item-${item.id}-external-badge`} className="ml-2 text-[10px] uppercase tracking-wider text-fg-secondary">
              CRA3D cannot provide
            </span>
          )}
        </p>
      </div>

      {(item.supporting_answers || []).length > 0 && answersById && questionMap && (
        <Collapsible open={openEvidence} onOpenChange={setOpenEvidence} className="mt-2">
          <CollapsibleTrigger asChild>
            <button
              data-testid={`sg-item-${item.id}-evidence-toggle`}
              className="flex items-center gap-1 text-xs text-fg-secondary hover:text-fg-primary transition-colors"
            >
              <ChevronDown className={"h-3 w-3 transition-transform " + (openEvidence ? "rotate-180" : "")} aria-hidden="true" />
              Show my answers ({item.supporting_answers.length})
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <ul className="space-y-2 pl-2 border-l border-border-subtle">
              {item.supporting_answers.map((qid) => {
                const q = questionMap[qid];
                const a = answersById[qid];
                const f = /^s(\d{1,3})_q(\d{1,3})$/i.exec(String(qid).trim());
                const secN = f ? parseInt(f[1], 10) : null;
                const qN = f ? parseInt(f[2], 10) : null;
                return (
                  <li key={qid} data-testid={`sg-item-${item.id}-evidence-${qid}`} className="text-xs text-fg-secondary pl-3">
                    <div className="flex flex-wrap items-baseline gap-2">
                      {f && (
                        <span
                          data-testid={`sg-item-${item.id}-evidence-${qid}-ref`}
                          data-qid={qid}
                          aria-label={`Section ${secN}, Question ${qN}`}
                          className="inline-block rounded border border-border-subtle bg-bg-base px-1.5 py-0.5 text-[11px] font-mono uppercase tracking-wide text-fg-primary whitespace-nowrap"
                        >
                          {`S${secN}_Q${qN}`}
                          <span className="sr-only"> (Section {secN}, Question {qN})</span>
                        </span>
                      )}
                      <p className="text-fg-primary">{q?.prompt || qid}</p>
                    </div>
                    {a && (
                      <p className="mt-1 italic">
                        {typeof a.answer === "string" ? a.answer : JSON.stringify(a.answer)}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </CollapsibleContent>
        </Collapsible>
      )}

      {item.priority_reason && (
        <p className="mt-2 text-xs text-fg-muted italic">Why: {item.priority_reason}</p>
      )}
      {item.reject_reason && (
        <p className="mt-2 text-xs text-fg-muted italic" data-testid={`sg-item-${item.id}-reject-reason`}>
          Your reject reason: {item.reject_reason}
        </p>
      )}

      {editOpen && (
        <div className="mt-3 space-y-2" data-testid={`sg-item-${item.id}-edit-form`}>
          <Textarea
            data-testid={`sg-item-${item.id}-edit-input`}
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            maxLength={400}
            className="bg-bg-base border-border-subtle text-fg-primary text-sm"
          />
          <p className="text-[11px] text-fg-muted italic">
            Mastery-language phrasing is stripped server-side so this always reads as
            "demonstrated" rather than "mastered".
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => send("correct", { new_label: newLabel.trim() })}
              disabled={busy || !newLabel.trim() || newLabel.trim() === item.label}
              data-testid={`sg-item-${item.id}-edit-save`}
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              Save
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => { setEditOpen(false); setNewLabel(displayLabel); }}
              data-testid={`sg-item-${item.id}-edit-cancel`}
              className="text-fg-secondary hover:text-fg-primary"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {!isRejected && !editOpen && (
          <Button
            size="sm" variant="ghost"
            onClick={() => setEditOpen(true)} disabled={busy}
            data-testid={`sg-item-${item.id}-edit-btn`}
            className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
          >
            <PenLine className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            {item.corrected ? "Edit again" : "Correct"}
          </Button>
        )}
        {item.corrected && !isRejected && (
          <Button
            size="sm" variant="ghost"
            onClick={() => send("uncorrect")} disabled={busy}
            data-testid={`sg-item-${item.id}-uncorrect-btn`}
            className="text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            Undo correction
          </Button>
        )}
        {!isRejected ? (
          <Button
            size="sm" variant="ghost"
            onClick={() => send("reject", { reason: reason.trim() || null })}
            disabled={busy}
            data-testid={`sg-item-${item.id}-reject-btn`}
            className="text-fg-secondary hover:text-destructive hover:bg-destructive/10"
          >
            <X className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            Not right
          </Button>
        ) : (
          <Button
            size="sm" variant="outline"
            onClick={() => send("unreject")} disabled={busy}
            data-testid={`sg-item-${item.id}-restore-btn`}
            className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            Restore
          </Button>
        )}
      </div>
    </li>
  );
}


function TeachesList({ gaps, directionId }) {
  if (!gaps || !gaps.length) {
    return <p className="text-xs text-fg-muted italic">No gap items to map yet.</p>;
  }
  const taught = gaps.filter((g) => (g.cra3d_teaches?.status) === "taught_by_cra3d");
  const notTaught = gaps.filter((g) => (g.cra3d_teaches?.status) === "not_currently_taught");

  return (
    <div className="space-y-4">
      <div data-testid={`sg-panel-${directionId}-teaches-covered`}>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border inline-block mb-2 border-progress-teal/40 bg-progress-teal/10 text-progress-teal">
          Covered by CRA3D
        </span>
        {taught.length === 0 ? (
          <p className="text-xs text-fg-muted italic">None of the current gap items have a direct match in CRA3D yet.</p>
        ) : (
          <ul className="space-y-2">
            {taught.map((g) => (
              <li key={g.id} data-testid={`sg-teaches-covered-${g.id}`} className="rounded-md border border-border-subtle bg-bg-base p-3">
                <p className="text-sm text-fg-primary">
                  <GraduationCap className="h-3.5 w-3.5 inline mr-1 text-progress-teal" aria-hidden="true" />
                  {g.corrected ? (g.corrected_label || g.label) : g.label}
                </p>
                <p className="mt-1 text-xs text-fg-secondary">{g.cra3d_teaches?.note}</p>
                {(g.cra3d_teaches?.taught_by || []).map((r, i) => (
                  <div key={i} className="mt-1 text-xs text-fg-secondary italic pl-3">
                    → {r.course_title} — {r.module_title}
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div data-testid={`sg-panel-${directionId}-teaches-notcovered`}>
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border inline-block mb-2 border-fg-muted/40 bg-bg-surface-hover text-fg-secondary">
          Not currently taught by CRA3D
        </span>
        {notTaught.length === 0 ? (
          <p className="text-xs text-fg-muted italic">Nothing here — every current gap has a CRA3D match.</p>
        ) : (
          <ul className="space-y-2">
            {notTaught.map((g) => (
              <li key={g.id} data-testid={`sg-teaches-notcovered-${g.id}`} className="rounded-md border border-border-subtle bg-bg-base p-3">
                <p className="text-sm text-fg-primary flex items-start gap-1">
                  <ExternalLink className="h-3.5 w-3.5 inline mr-1 mt-0.5 text-fg-secondary" aria-hidden="true" />
                  {g.corrected ? (g.corrected_label || g.label) : g.label}
                </p>
                <p className="mt-1 text-xs text-fg-muted italic">{g.cra3d_teaches?.note}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default SkillGapPanel;
